import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router, UrlSegment } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { ConfigService } from '@app-config';
import { IDENTITY_MENU_PARAMS, IdentityClientService, IdentityConfigurationService, IdentityService, NavigationAccessStore } from '@auth';
import { AppInitValues, SharedConstants } from '@constants';
import { EventService } from '@core';
import { ErrorService } from '@services';

const ERROR_ROUTE = 'error';

const MODULES: any = Object.freeze({
    'tpo': 'TP',
    'buildingcontrol': 'BR',
    'enforcements': 'EN',
    'reporting': 'REPORTING',
    'planning': 'PA',
    'preapps': 'PR',
    'built-environment': 'CPW',
    'publicprotection': 'PP',
    'landcharges': 'LC',
    'gisadminsite': 'GIS',
    'synchronizer': 'SYNC',
    'configurator': 'CONFIGURATOR',
    'hmlr': 'HMLR',
    'built-environment-configurator': 'BEC',
    'initialnotice': 'IN'
});

export const moduleResolver: ResolveFn<void | RedirectCommand> = async (route, state) => {
    const identityConfigService = inject(IdentityConfigurationService);
    const identityClientService = inject(IdentityClientService);
    const eventService = inject(EventService);
    const configService = inject(ConfigService);
    const identityService = inject(IdentityService);
    const errorService = inject(ErrorService);
    const navigationAccessStore = inject(NavigationAccessStore);
    const menuParams = inject(IDENTITY_MENU_PARAMS);
    const router = inject(Router);

    // Pathless/root module routes have no URL segment to derive the module from
    const moduleCode = route.data['moduleCode'] ?? getModuleCode(route.url);
    identityConfigService.setService(moduleCode);

    try {
        await configService.getModuleConfig();
    }
    catch {
        // Without the module configuration the page cannot render; the error page is not behind the auth guard, so this cannot loop.
        errorService.setHasError(true);

        return new RedirectCommand(router.parseUrl(`/${identityClientService.get().url}/${ERROR_ROUTE}`));
    }

    // Invalidate first so every getMenu() triggered by this reload (sas-menu, landpage, etc.) shares
    // a single fresh GET /api/menu instead of each one firing its own request.
    identityService.invalidateMenuCache();
    eventService.emitEvent(SharedConstants.RELOAD_MENU_EVENT, {});

    // Same request the menu components just made (cached), so this adds no extra call.
    try {
        const menu = await firstValueFrom(identityService.getMenu(menuParams));

        if (moduleCode === AppInitValues.INITIAL_SERVICE) {
            navigationAccessStore.setModules(menu);
        }
        else {
            navigationAccessStore.setModuleOptions(moduleCode, menu);
        }
    }
    catch {
        // The menu components report their own load errors; the store just stays empty.
    }

    return undefined;
};

function getModuleCode(segments: UrlSegment[]): string {
    const moduleCode = segments[0].path.toLowerCase();

    return MODULES[moduleCode] || 'HUB';
}
